import { useEffect, useState } from "react";
import { Search, Plus, AlertTriangle, Package2, Boxes, TrendingDown, MoreHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatRupiah } from "@/lib/dummyData";
import { ProductAPI } from "@/lib/api";
import { Inbox } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = { sku: "", product_name: "", category: "Sembako", image_url: "", description: "", price_base_cost: "", price_non_member: "", price_member: "", stock_quantity: "" };

const statusStyle = {
  "Aktif": "bg-sage/20 text-forest",
  "Stok Rendah": "bg-amber-warm/20 text-amber-700",
  "Kritis": "bg-terracotta/15 text-terracotta",
};

// Status stok diturunkan dari jumlah stok.
const stockStatus = (s) => (s > 50 ? "Aktif" : s > 20 ? "Stok Rendah" : "Kritis");

export default function AdminProducts() {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  // Edit info tampilan produk (nama, kategori, gambar, deskripsi) — harga tidak diubah di sini.
  const [editForm, setEditForm] = useState(null);

  // Ambil katalog nyata dari backend; petakan ke bentuk tabel admin.
  const load = () =>
    ProductAPI.list().then((rows) => {
      setProducts((rows || []).map((p) => ({
        id: String(p.id),
        name: p.name,
        category: p.category,
        image: p.image,
        description: p.description || "",
        supplier: p.source_type === "VENDOR_ANGGOTA" ? "Vendor Anggota" : "Koperasi Pusat",
        costPrice: null,
        price: Number(p.price_non_member),
        memberPrice: Number(p.price_member),
        stock: p.stock,
        status: stockStatus(p.stock),
      })));
    }).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  // Tambah produk baru ke katalog.
  const submitEdit = async () => {
    if (!editForm.product_name) return toast.error("Nama produk wajib diisi");
    setSaving(true);
    try {
      const { id, ...payload } = editForm;
      await ProductAPI.update(id, payload);
      toast.success("Produk diperbarui");
      setEditForm(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal memperbarui produk");
    } finally { setSaving(false); }
  };

  const submitAdd = async () => {
    if (!form.sku || !form.product_name || form.price_non_member === "" || form.price_member === "") {
      return toast.error("Isi SKU, nama, harga publik, dan harga anggota.");
    }
    setSaving(true);
    try {
      await ProductAPI.create({
        sku: form.sku, product_name: form.product_name, category: form.category, image_url: form.image_url,
        description: form.description,
        price_base_cost: Number(form.price_base_cost || 0), price_non_member: Number(form.price_non_member),
        price_member: Number(form.price_member), stock_quantity: Number(form.stock_quantity || 0),
      });
      toast.success("Produk ditambahkan");
      setAddOpen(false); setForm(emptyProduct); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menambah produk");
    } finally { setSaving(false); }
  };

  // Restok manual (+50) via PATCH.
  const restock = async (id) => {
    const p = products.find((x) => x.id === id);
    try { await ProductAPI.update(id, { stock_quantity: (p?.stock || 0) + 50 }); toast.success("Stok +50"); load(); }
    catch { toast.error("Gagal restok"); }
  };

  const list = products;
  const filtered = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Manajemen Produk & Stok</h1>
          <p className="text-ink/75 mt-2 text-sm">Katalog harga ganda, alert stok, manajemen supplier.</p>
        </div>
        <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="products-add">
          <Plus size={15} /> Tambah Produk
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-xl border-stone-soft p-5">
          <div className="w-9 h-9 rounded-lg bg-forest/10 text-forest grid place-items-center"><Package2 size={17} /></div>
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">Total Produk</div>
          <div className="font-display font-bold text-2xl text-ink mt-1">{list.length}</div>
        </Card>
        <Card className="rounded-xl border-stone-soft p-5">
          <div className="w-9 h-9 rounded-lg bg-sage/20 text-forest grid place-items-center"><Boxes size={17} /></div>
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">Stok Sehat</div>
          <div className="font-display font-bold text-2xl text-ink mt-1">{list.filter((p) => p.status === "Aktif").length}</div>
        </Card>
        <Card className="rounded-xl border-stone-soft p-5">
          <div className="w-9 h-9 rounded-lg bg-amber-warm/15 text-amber-700 grid place-items-center"><TrendingDown size={17} /></div>
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">Stok Rendah</div>
          <div className="font-display font-bold text-2xl text-ink mt-1">{list.filter((p) => p.status === "Stok Rendah").length}</div>
        </Card>
        <Card className="rounded-xl border-stone-soft p-5">
          <div className="w-9 h-9 rounded-lg bg-terracotta/15 text-terracotta grid place-items-center"><AlertTriangle size={17} /></div>
          <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-3">Stok Kritis</div>
          <div className="font-display font-bold text-2xl text-ink mt-1 flex items-center gap-2">{list.filter((p) => p.status === "Kritis").length} <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse"></span></div>
        </Card>
      </div>

      <Card className="rounded-xl border-stone-soft p-5">
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className="pl-9 bg-cream border-stone-soft rounded-lg" data-testid="products-search" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">Produk</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Kategori</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Supplier</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">HPP</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Harga Publik</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Harga Anggota</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-center">Stok</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} className="border-stone-soft hover:bg-cream/30" data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-cover bg-cream" />
                      <div>
                        <div className="font-semibold text-sm text-ink line-clamp-1 max-w-[230px]">{p.name}</div>
                        <div className="font-mono text-[10px] text-ink/55">{p.id.toUpperCase()}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge className="bg-stone-soft/40 text-ink/75 hover:bg-stone-soft/40 border-0 rounded-full text-[10px]">{p.category}</Badge></TableCell>
                  <TableCell className="text-sm text-ink/75">{p.supplier}</TableCell>
                  <TableCell className="text-right text-sm text-ink/75">{p.costPrice != null ? formatRupiah(p.costPrice) : "—"}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-ink">{formatRupiah(p.price)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-amber-700">{formatRupiah(p.memberPrice)}</TableCell>
                  <TableCell className="text-center">
                    <span className={`font-display font-bold ${p.status === "Kritis" ? "text-terracotta" : p.status === "Stok Rendah" ? "text-amber-700" : "text-ink"}`}>{p.stock}</span>
                  </TableCell>
                  <TableCell><Badge className={`${statusStyle[p.status]} hover:${statusStyle[p.status]} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{p.status}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-lg hover:bg-cream"><MoreHorizontal size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem onClick={() => setEditForm({ id: p.id, product_name: p.name, category: p.category, image_url: p.image || "", description: p.description })} data-testid={`product-edit-${p.id}`}>Edit Info & Deskripsi</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => restock(p.id)} data-testid={`product-restock-${p.id}`}>Restok Manual (+50)</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toast.info("Riwayat mutasi (segera hadir)")}>Riwayat Mutasi</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loaded && filtered.length === 0 && (
            <div className="py-12 text-center">
              <Inbox className="mx-auto text-ink/30" size={28} />
              <p className="text-sm text-ink/55 mt-2">{q ? "Tidak ada produk yang cocok" : "Belum ada produk — klik 'Tambah Produk'"}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Dialog Tambah Produk */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Tambah Produk</DialogTitle>
            <DialogDescription>Produk baru langsung terbit di katalog dengan harga ganda (publik & anggota).</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-lg" data-testid="add-product-sku" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-lg" data-testid="add-product-cat" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Nama Produk</Label><Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="rounded-lg" data-testid="add-product-name" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">URL Gambar</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-lg" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Deskripsi</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Isi/berat kemasan, asal produk, cara penyimpanan, dll." className="rounded-lg" data-testid="add-product-desc" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Harga Pokok (HPP)</Label><Input type="number" value={form.price_base_cost} onChange={(e) => setForm({ ...form, price_base_cost: e.target.value })} className="rounded-lg" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Stok Awal</Label><Input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} className="rounded-lg" data-testid="add-product-stock" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Harga Publik</Label><Input type="number" value={form.price_non_member} onChange={(e) => setForm({ ...form, price_non_member: e.target.value })} className="rounded-lg" data-testid="add-product-price" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Harga Anggota</Label><Input type="number" value={form.price_member} onChange={(e) => setForm({ ...form, price_member: e.target.value })} className="rounded-lg" data-testid="add-product-member-price" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitAdd} disabled={saving} data-testid="add-product-submit">
              {saving ? "Menyimpan…" : "Simpan Produk"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Info Produk</DialogTitle>
            <DialogDescription>Nama, kategori, gambar, dan deskripsi yang tampil di toko & katalog.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid gap-4 mt-2">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Nama Produk</Label><Input value={editForm.product_name} onChange={(e) => setEditForm({ ...editForm, product_name: e.target.value })} className="rounded-lg" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Kategori</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="rounded-lg" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">URL Gambar</Label><Input value={editForm.image_url} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })} className="rounded-lg" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Deskripsi</Label><Textarea rows={5} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="rounded-lg" data-testid="edit-product-desc" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditForm(null)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitEdit} disabled={saving} data-testid="edit-product-submit">
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
